import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';

const AcceptInvitation = () => {
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
        // Extract email from token by calling backend
        const getInvitationEmail = async () => {
            try {
                const response = await api.get(`/api/team/invitation-email?token=${token}`);
                setEmail(response.data);
            } catch (err) {
                setError('Invalid or expired invitation link!');
            }
        };
        getInvitationEmail();
    }, [token, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match!');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters!');
            return;
        }

        setLoading(true);
        try {
            await api.post(`/api/team/accept?token=${token}&password=${password}`);
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to accept invitation!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center px-6">
            <div className="w-full max-w-md">

                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/">
                        <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                            TubeScript AI
                        </span>
                    </Link>
                    <p className="text-gray-400 mt-2">You've been invited to join a team!</p>
                </div>

                <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                    {success ? (
                        <div className="text-center">
                            {/* <p className="text-5xl mb-4"></p> */}
                            <h2 className="text-xl font-bold mb-2">Account Created!</h2>
                            <p className="text-gray-400 mb-6">
                                Your account has been created successfully!
                                Redirecting to login in 3 seconds...
                            </p>
                            <Link to="/login" className="text-purple-400 hover:text-purple-300 transition">
                                Go to Login
                            </Link>
                        </div>
                    ) : (
                        <>
                            {email && (
                                <div className="bg-gray-900 rounded-xl p-3 mb-6 text-center">
                                    <p className="text-gray-400 text-sm">Joining as:</p>
                                    <p className="text-white font-semibold">{email}</p>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Set Your Password
                                    </label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Confirm Password
                                    </label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition">
                                    {loading ? 'Creating Account...' : 'Create Account & Join Team'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;


